import { Breadcrumb, Button, Divider, Empty, Input, Layout, Modal, Skeleton, Table, Tag, notification, theme } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { apiSyncZoopPlans, apiZoopPlans } from '../../../api/zoopPlan';
import { isSuperUser } from '../../../helpers/user';
import './ZoopBasePlan.scss';

const { Content } = Layout;

const ZoopBasePlan = () => {
  const [plans, setPlans] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const searchTimer = useRef(null);
  const navigate = useNavigate();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const allowed = isSuperUser();

  const loadPlans = async (page, search) => {
    setLoading(true);
    try {
      const json = await apiZoopPlans({ page, search: search || '' });
      setPlans(json.results || []);
      setPagination(prev => ({
        ...prev,
        total: json.count || 0,
        current: page,
      }));
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Ocorreu um erro ao buscar os planos base.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSearchChange = (value) => {
    setSearchText(value);
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    searchTimer.current = setTimeout(() => {
      loadPlans(1, value);
    }, 400);
  };

  const onSearchSubmit = (value) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    loadPlans(1, value);
  };

  useEffect(() => {
    if (allowed) {
      loadPlans(1, '');
    }
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    };
  }, []);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  const runSync = async () => {
    setSyncing(true);
    try {
      const { status, data } = await apiSyncZoopPlans();
      if (status === 201) {
        notification.success({
          message: 'Sucesso!',
          description: data?.message || 'Sincronização iniciada.',
        });
        await loadPlans(1, searchText);
      } else if (status === 200) {
        notification.info({
          message: 'Sincronização em andamento',
          description: data?.message || 'Já existe uma sincronização em andamento.',
        });
      } else {
        notification.error({
          message: 'Erro',
          description: 'Não foi possível iniciar a sincronização.',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Não foi possível iniciar a sincronização.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleSync = () => {
    Modal.confirm({
      title: 'Sincronizar com a Zoop',
      content: 'Isso irá buscar todos os planos base na Zoop e atualizar a listagem. Deseja prosseguir?',
      okText: 'Sim, sincronizar',
      cancelText: 'Cancelar',
      onOk: runSync,
    });
  };

  const openPlan = (record) => navigate(`/planos-base-zoop/detalhe/${record.id}`);

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <p
          onClick={() => openPlan(record)}
          style={{ textTransform: 'uppercase', cursor: 'pointer' }}
          dangerouslySetInnerHTML={{ __html: name }}
        />
      ),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      render: (description, record) => (
        <p
          onClick={() => openPlan(record)}
          style={{ cursor: 'pointer' }}
          dangerouslySetInnerHTML={{ __html: description || '-' }}
        />
      ),
    },
    {
      title: 'Ativo',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Ativo' : 'Inativo'}</Tag>
      ),
    },
  ];

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  return (
    <div className='ZoopBasePlan'>
      <Layout style={{ padding: '1.25rem' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <h2 className='title-page'>Planos base Zoop</h2>
          <Divider />
          <Breadcrumb.Item>Operação</Breadcrumb.Item>
          <Breadcrumb.Item>Planos base Zoop</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="box-divider">
            <h2 className='subtitle-page'>Planos base Zoop</h2>
            {isSuperUser() && (
              <Button type="primary" loading={syncing} onClick={handleSync}>
                Sincronizar com a Zoop
              </Button>
            )}
          </div>
          <Divider />
          <Input.Search
            placeholder='Pesquisar'
            allowClear
            enterButton
            value={searchText}
            onSearch={onSearchSubmit}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ marginBottom: 16, maxWidth: '200px' }}
          />
          {loading ? (
            <Skeleton active />
          ) : (
            <Table
              columns={columns}
              locale={locale}
              dataSource={plans.map((item) => ({ ...item, key: item.id }))}
              pagination={{
                current: pagination.current,
                total: pagination.total,
                showSizeChanger: false,
                onChange: (page) => loadPlans(page, searchText),
              }}
              bordered
            />
          )}
        </Content>
        <Divider />
      </Layout>
    </div>
  );
};

export default ZoopBasePlan;
